import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { EmailSuggestField } from '../EmailSuggestField';
import { Icon } from '../Icon';
import { MultiSelect } from '../MultiSelect';
import { shouldOfferKeepClientRequester } from '../../utils/changeRequester';
import {
    clientContactRoleShortcuts,
    roleLabelKey,
    type ClientWithContacts,
} from '../../utils/clientContacts';
import { requesterPath } from '../../utils/requesterPath';
import type { RequesterEmailSource } from '../../utils/quickCreatePrefs';

export type ReplyRecipientsShape = {
    to?: {
        emails?: string[];
        signalUsers?: string[];
        signalGroups?: string[];
    } | null;
    cc?: {
        emails?: string[];
        signalUsers?: string[];
        signalGroups?: string[];
    } | null;
};

export type ReplyRecipientsEditorProps = {
    replyRecipients: ReplyRecipientsShape;
    toEmailInput: string;
    ccEmailInput: string;
    onToEmailInputChange: (value: string) => void;
    onCcEmailInputChange: (value: string) => void;
    onAddEmail: (section: 'to' | 'cc', email: string) => void;
    onRemoveEmail: (section: 'to' | 'cc', email: string) => void;
    onUpdateSignalUsers: (userIds: string[]) => void;
    onUpdateSignalGroups: (groupIds: string[]) => void;
    recipientEmailSources: RequesterEmailSource[];
    /** Client email currently shown as requester because the card has no To recipient. */
    implicitClientEmail?: string | null;
    /** Linked Zone Client with address-book contacts (Facturation / Administratif / …). */
    linkedClient?: ClientWithContacts | null;
    memberOptions: Array<{ value: string; label: string }>;
    groupOptions: Array<{ value: string; label: string }>;
    isSaving?: boolean;
    onSave: () => void;
    /** When true, hide the persist button (ephemeral recipients, e.g. forward). */
    hideSave?: boolean;
    className?: string;
};

/** Shared À / Cc / Signal editor for reply recipients (header + composer). */
function ReplyRecipientsEditor(props: ReplyRecipientsEditorProps) {
    const { t } = useTranslation();
    const {
        replyRecipients,
        toEmailInput,
        ccEmailInput,
        onToEmailInputChange,
        onCcEmailInputChange,
        onAddEmail,
        onRemoveEmail,
        onUpdateSignalUsers,
        onUpdateSignalGroups,
        recipientEmailSources,
        implicitClientEmail = null,
        linkedClient = null,
        memberOptions,
        groupOptions,
        isSaving = false,
        onSave,
        hideSave = false,
        className = 'mt-3 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 p-3',
    } = props;

    const offerKeepClientRequester = shouldOfferKeepClientRequester(
        implicitClientEmail,
        replyRecipients?.to?.emails,
    );

    const roleShortcuts = clientContactRoleShortcuts(
        linkedClient,
        replyRecipients?.to?.emails || [],
    );

    return (
        <div className={className}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">{t('changes.reply_recipients_to')}</label>
                    <div className="mt-1">
                        <EmailSuggestField
                            value={toEmailInput}
                            onChange={onToEmailInputChange}
                            onCommit={(email) => onAddEmail('to', email)}
                            sources={recipientEmailSources}
                            excludedEmails={replyRecipients?.to?.emails || []}
                            placeholder={t('changes.reply_add_email_placeholder') || ''}
                        />
                    </div>
                    {roleShortcuts.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {roleShortcuts.map(({ role, email }) => (
                                <Button
                                    key={`role-${role}-${email}`}
                                    title={t('changes.reply_add_contact_role', {
                                        role: t(roleLabelKey(role)),
                                    }) || ''}
                                    color="default"
                                    small
                                    onClick={() => onAddEmail('to', email)}
                                />
                            ))}
                        </div>
                    )}
                    {offerKeepClientRequester && implicitClientEmail && (
                        <div className="mt-2 rounded border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 px-2 py-2 text-xs text-amber-900 dark:text-amber-100">
                            <p>{t('changes.reply_implicit_requester_replaced', { email: implicitClientEmail })}</p>
                            <div className="mt-2">
                                <Button
                                    title={t('changes.reply_implicit_requester_keep') || ''}
                                    color="warning"
                                    small
                                    onClick={() => onAddEmail('to', implicitClientEmail)}
                                />
                            </div>
                        </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                        {(replyRecipients?.to?.emails || []).map((email: string) => (
                            <span key={`to-${email}`} className="inline-flex items-center rounded bg-blue-100 text-blue-900 dark:bg-blue-900/50 dark:text-blue-100 px-2 py-1 text-xs">
                                <Link
                                    to={requesterPath(email)}
                                    className="hover:underline"
                                    title={t('requester.title') || email}
                                >
                                    {email}
                                </Link>
                                <button className="ml-1 inline-flex text-current" type="button" onClick={() => onRemoveEmail('to', email)} aria-label={t('common.delete') || ''}>
                                    <Icon type="close" size={3} color="inherit" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">{t('changes.reply_recipients_cc')}</label>
                    <div className="mt-1">
                        <EmailSuggestField
                            value={ccEmailInput}
                            onChange={onCcEmailInputChange}
                            onCommit={(email) => onAddEmail('cc', email)}
                            sources={recipientEmailSources}
                            excludedEmails={replyRecipients?.cc?.emails || []}
                            placeholder={t('changes.reply_add_email_placeholder') || ''}
                        />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {(replyRecipients?.cc?.emails || []).map((email: string) => (
                            <span key={`cc-${email}`} className="inline-flex items-center rounded bg-purple-100 text-purple-900 dark:bg-purple-900/50 dark:text-purple-100 px-2 py-1 text-xs">
                                {email}
                                <button className="ml-1 inline-flex text-current" type="button" onClick={() => onRemoveEmail('cc', email)} aria-label={t('common.delete') || ''}>
                                    <Icon type="close" size={3} color="inherit" />
                                </button>
                            </span>
                        ))}
                    </div>
                </div>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">{t('changes.reply_signal_users')}</label>
                    <div className="mt-1">
                        <MultiSelect
                            options={memberOptions}
                            defaultValue={replyRecipients?.to?.signalUsers || []}
                            placeholder={t('changes.reply_signal_users') || ''}
                            onChange={(value) => onUpdateSignalUsers(value as string[])}
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">{t('changes.reply_signal_groups')}</label>
                    <div className="mt-1">
                        <MultiSelect
                            options={groupOptions}
                            defaultValue={replyRecipients?.to?.signalGroups || []}
                            placeholder={t('changes.reply_signal_groups') || ''}
                            onChange={(value) => onUpdateSignalGroups(value as string[])}
                        />
                    </div>
                </div>
            </div>
            {!hideSave && (
                <div className="mt-3 flex justify-end">
                    <Button
                        title={isSaving ? (t('changes.reply_recipients_saving') || '') : (t('changes.reply_recipients_save') || '')}
                        icon="save"
                        color="secondary"
                        small
                        loading={isSaving}
                        onClick={onSave}
                    />
                </div>
            )}
        </div>
    );
}

export default ReplyRecipientsEditor;
